/**
 * Glossary of technical terms used in the MIBiG database
 * Used for tooltips and help text
 */

export interface GlossaryEntry {
  term: string
  definition: string
  shortDef?: string  // Shorter version for compact tooltips
}

export const glossary: Record<string, GlossaryEntry> = {
  // BLAST statistics
  evalue: {
    term: 'E-value',
    definition: 'Expected number of alignments with this score or better that would occur by chance in a database of this size. Lower values indicate more significant matches.',
    shortDef: 'Lower = more significant match',
  },
  identity: {
    term: 'Identity',
    definition: 'Percentage of identical amino acids between query and hit sequences in the aligned region.',
    shortDef: '% identical amino acids',
  },
  coverage: {
    term: 'Coverage',
    definition: 'Percentage of the query sequence that aligns with the hit sequence. High coverage indicates the hit spans most of the query.',
    shortDef: '% of query aligned',
  },
  bitscore: {
    term: 'Bit Score',
    definition: 'Normalized alignment score that indicates the quality of the alignment. Higher values indicate better alignments.',
    shortDef: 'Higher = better alignment',
  },
  hsp: {
    term: 'HSP',
    definition: 'High-Scoring Segment Pair. A local alignment with no gaps that achieves a score above a threshold.',
    shortDef: 'High-scoring alignment segment',
  },

  // AlphaFold/Structure metrics
  plddt: {
    term: 'pLDDT',
    definition: 'Predicted Local Distance Difference Test score (0-100). AlphaFold\'s per-residue confidence score. >90: very high confidence, 70-90: confident, 50-70: low confidence, <50: very low confidence.',
    shortDef: 'AlphaFold confidence (0-100)',
  },
  iptm: {
    term: 'ipTM',
    definition: 'Interface predicted TM-score. Measures the predicted quality of the protein-protein interface. Values ≥0.8 indicate high-confidence interfaces, ≥0.7 are confident, <0.6 are low confidence.',
    shortDef: 'Interface confidence (≥0.8 = high)',
  },
  ipsae: {
    term: 'ipSAE',
    definition: 'Interface predicted Structural Accuracy Estimate. Evaluates the predicted accuracy of the protein interface structure. Higher values indicate better predictions.',
    shortDef: 'Interface accuracy score',
  },
  ipsae_min: {
    term: 'ipSAE_min',
    definition: 'Minimum ipSAE score across the interface. Represents the worst-case accuracy estimate for the interface.',
    shortDef: 'Minimum interface accuracy',
  },
  rmsd: {
    term: 'RMSD',
    definition: 'Root Mean Square Deviation. Measures the average distance between aligned atoms. Lower values indicate more similar structures.',
    shortDef: 'Lower = more similar structures',
  },
  tm_score: {
    term: 'TM-score',
    definition: 'Template Modeling score. Measures global structural similarity (0-1). >0.5 indicates same fold, >0.17 indicates same superfamily.',
    shortDef: '>0.5 = same fold',
  },

  // Domain annotations
  pssm_id: {
    term: 'PSSM ID',
    definition: 'Position-Specific Scoring Matrix ID from NCBI\'s Conserved Domain Database (CDD). Links to detailed domain information.',
    shortDef: 'CDD domain identifier',
  },
  cdd: {
    term: 'CDD',
    definition: 'Conserved Domain Database. NCBI resource for identifying conserved protein domains and functional sites.',
    shortDef: 'NCBI domain database',
  },

  // Enzyme/Reaction terms
  ec_number: {
    term: 'EC Number',
    definition: 'Enzyme Commission number. A numerical classification scheme for enzymes based on the chemical reactions they catalyze.',
    shortDef: 'Enzyme classification',
  },
  rhea: {
    term: 'Rhea',
    definition: 'Expert-curated database of biochemical reactions. Each Rhea ID represents a specific chemical transformation.',
    shortDef: 'Biochemical reaction database',
  },
  chebi: {
    term: 'ChEBI',
    definition: 'Chemical Entities of Biological Interest. Database of molecular entities focused on small chemical compounds.',
    shortDef: 'Chemical compound database',
  },
  cofactor: {
    term: 'Cofactor',
    definition: 'Non-protein chemical compound required for enzyme activity. Includes metal ions and organic molecules.',
    shortDef: 'Required for enzyme activity',
  },

  // Biosynthesis terms
  biosyn_class: {
    term: 'Biosynthetic Class',
    definition: 'Classification of biosynthetic gene clusters based on the type of natural product they produce (e.g., NRPS, PKS, RiPP, Terpene).',
    shortDef: 'Natural product type',
  },
  nrps: {
    term: 'NRPS',
    definition: 'Non-Ribosomal Peptide Synthetase. Large multimodular enzymes that synthesize peptides without ribosomal machinery.',
    shortDef: 'Non-ribosomal peptide synthesis',
  },
  pks: {
    term: 'PKS',
    definition: 'Polyketide Synthase. Enzyme systems that produce polyketide natural products through repeated condensation reactions.',
    shortDef: 'Polyketide synthesis',
  },
  ripp: {
    term: 'RiPP',
    definition: 'Ribosomally synthesized and Post-translationally modified Peptide. Natural products derived from ribosomally synthesized precursor peptides.',
    shortDef: 'Modified ribosomal peptide',
  },
  terpene: {
    term: 'Terpene',
    definition: 'Natural products derived from isoprene units. Includes monoterpenes, sesquiterpenes, diterpenes, and larger terpenoids.',
    shortDef: 'Isoprene-derived compound',
  },

  // Function prediction
  fujisan: {
    term: 'FUJISAN',
    definition: 'Function prediction method based on structural similarity. Uses AlphaFold structures to identify proteins with similar 3D folds and potentially similar functions.',
    shortDef: 'Structure-based function prediction',
  },
  unkai: {
    term: 'UNKAI',
    definition: 'Function prediction method based on protein sequence embeddings. Uses machine learning to identify proteins with similar sequence features.',
    shortDef: 'Embedding-based function prediction',
  },

  // Database identifiers
  mibig: {
    term: 'MIBiG',
    definition: 'Minimum Information about a Biosynthetic Gene cluster. Repository of curated biosynthetic gene cluster information.',
    shortDef: 'Biosynthetic gene cluster database',
  },
  uniprot: {
    term: 'UniProt',
    definition: 'Universal Protein Resource. Comprehensive database of protein sequence and functional information.',
    shortDef: 'Protein sequence database',
  },
  swissprot: {
    term: 'SwissProt',
    definition: 'Manually curated section of UniProt. Contains high-quality protein annotations reviewed by curators.',
    shortDef: 'Curated protein database',
  },
  pdb: {
    term: 'PDB',
    definition: 'Protein Data Bank. Archive of experimentally determined 3D structures of proteins, nucleic acids, and complex assemblies.',
    shortDef: 'Protein structure database',
  },

  // Interaction types
  homo: {
    term: 'Homo',
    definition: 'Homodimer or homo-oligomer interaction. Protein interacting with another copy of itself.',
    shortDef: 'Self-interaction',
  },
  hetero: {
    term: 'Hetero',
    definition: 'Heterodimer or hetero-oligomer interaction. Interaction between two different protein chains.',
    shortDef: 'Different protein interaction',
  },
}

/**
 * Get glossary entry by key
 */
export function getGlossaryEntry(key: string): GlossaryEntry | undefined {
  return glossary[key.toLowerCase()]
}

/**
 * Get short definition or fall back to full definition
 */
export function getShortDefinition(key: string): string | undefined {
  const entry = getGlossaryEntry(key)
  return entry?.shortDef || entry?.definition
}

export default glossary
